import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { 
  DndContext, 
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable
} from '@dnd-kit/core';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { 
  Plus, 
  Calendar as CalendarIcon,
  Truck,
  User,
  MapPin,
  ClipboardList,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Edit,
  Trash2,
  RefreshCw,
  Briefcase,
  Users,
  Send,
  Check,
  X,
  Phone,
  FileText,
  MessageCircle,
  Clock,
  Package,
  AlertTriangle,
  Eye,
  Receipt,
  FileCheck,
  Navigation,
  GripVertical
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import SmartDispatchDialog from '../components/SmartDispatchDialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Uncontrolled note editor. Uses defaultValue + a ref so the input's value
 * lives in the DOM rather than React state. This is immune to the "cursor
 * jumps to position 0" bug that controlled inputs get when nested inside a
 * dnd-kit context that re-renders on every drag-state change (which produced
 * the "reversed typing" issue on the dispatch board).
 */
function InvoiceNoteEditor({ initialValue, onSave, onCancel, testKey }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    // Focus + place caret at the end on open. Not autoFocus (which re-fires
    // on parent re-render in some browsers, resetting the cursor).
    if (ref.current) {
      ref.current.focus();
      const len = ref.current.value.length;
      try { ref.current.setSelectionRange(len, len); } catch { /* ignore */ }
    }
  }, []);

  const save = () => onSave(ref.current ? ref.current.value : '');

  return (
    <div className="flex items-start gap-1 flex-1">
      <textarea
        ref={ref}
        defaultValue={initialValue}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            save();
          }
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        className="flex-1 min-h-[48px] text-xs rounded border border-input bg-background text-foreground px-2 py-1 resize-y"
        placeholder="Extra ticket #, misc notes... (Ctrl/Cmd+Enter to save)"
        data-testid={`invoice-note-input-${testKey}`}
      />
      <div className="flex flex-col gap-0.5">
        <button
          type="button"
          onClick={save}
          className="h-5 w-5 p-0 inline-flex items-center justify-center hover:bg-muted rounded"
          title="Save (Ctrl/Cmd+Enter)"
          data-testid={`invoice-note-save-${testKey}`}
        >
          <Check className="h-3 w-3 text-green-500" />
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="h-5 w-5 p-0 inline-flex items-center justify-center hover:bg-muted rounded"
          title="Cancel (Esc)"
          data-testid={`invoice-note-cancel-${testKey}`}
        >
          <X className="h-3 w-3 text-red-500" />
        </button>
      </div>
    </div>
  );
}

// Draggable Item Component using dnd-kit
function DraggableItem({ id, type, children, data }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `${type}-${id}`,
    data: { type, ...data }
  });
  
  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    zIndex: 1000,
    pointerEvents: 'none', // Prevent interaction while dragging
  } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`touch-none select-none ${isDragging ? 'opacity-50 cursor-grabbing' : 'cursor-grab'}`}
    >
      {children}
    </div>
  );
}

// Droppable Zone Component using dnd-kit with enhanced highlight
function DroppableZone({ id, children, className }) {
  const { isOver, setNodeRef, active } = useDroppable({ id });
  
  return (
    <div
      ref={setNodeRef}
      className={`${className} transition-all duration-150 ${
        isOver 
          ? 'ring-2 ring-blue-400 bg-blue-500/20 scale-[1.01]' 
          : active 
            ? 'ring-1 ring-blue-400/30' 
            : ''
      }`}
    >
      {children}
    </div>
  );
}

// Droppable Assignment Card - can receive units or drivers dropped onto it
function DroppableAssignmentCard({ assignment, children, className, onDrop }) {
  const { isOver, setNodeRef, active } = useDroppable({ 
    id: `assignment:${assignment.id}`,
    data: { type: 'assignment', assignment }
  });
  
  // Determine what can be dropped
  // Units: can have up to 2 (unit_name + support_equipment_name)
  const hasUnit1 = assignment.unit_name || assignment.unit_id;
  const hasUnit2 = assignment.support_equipment_name || assignment.support_equipment_id;
  const canReceiveUnit = !hasUnit1 || !hasUnit2; // Can receive if either slot is empty
  
  // Drivers: only 1 per assignment
  const hasDriver = assignment.driver_id || assignment.driver?.id;
  const canReceiveDriver = !hasDriver;
  
  // Check what's being dragged
  const dragType = active?.data?.current?.type;
  const canAcceptCurrentDrag = (dragType === 'unit' && canReceiveUnit) || 
                               (dragType === 'driver' && canReceiveDriver);
  
  // Determine which slot the unit would go into
  const unitSlot = !hasUnit1 ? 'primary' : !hasUnit2 ? 'support' : null;
  
  return (
    <div
      ref={setNodeRef}
      className={`${className} transition-all duration-150 ${
        isOver && canAcceptCurrentDrag
          ? 'ring-2 ring-green-400 bg-green-500/20 scale-[1.02]' 
          : active && (canReceiveUnit || canReceiveDriver)
            ? 'ring-1 ring-green-400/30' 
            : ''
      }`}
    >
      {children}
      {isOver && canAcceptCurrentDrag && dragType === 'unit' && (
        <div className="text-xs text-green-400 mt-1 font-medium">
          ✓ Drop as {unitSlot === 'primary' ? 'primary unit' : 'support equipment'}
        </div>
      )}
      {isOver && canAcceptCurrentDrag && dragType === 'driver' && (
        <div className="text-xs text-green-400 mt-1 font-medium">
          ✓ Drop to assign driver
        </div>
      )}
      {isOver && !canAcceptCurrentDrag && dragType === 'unit' && (
        <div className="text-xs text-red-400 mt-1">
          ✗ Already has 2 units
        </div>
      )}
      {isOver && !canAcceptCurrentDrag && dragType === 'driver' && (
        <div className="text-xs text-red-400 mt-1">
          ✗ Already has a driver
        </div>
      )}
    </div>
  );
}

// Updated status options matching the spreadsheet
const STATUS_OPTIONS = [
  { value: 'NOT_ASSIGNED', label: 'Not Assigned', color: 'bg-slate-500' },
  { value: 'PENDING', label: 'Pending Confirmation', color: 'bg-amber-500' },
  { value: 'CONFIRMED', label: 'Confirmed', color: 'bg-blue-500' },
  { value: 'DRIVING', label: 'Driving', color: 'bg-purple-500' },
  { value: 'COMPLETE', label: 'Complete', color: 'bg-emerald-600' }
];

// Product/Material options
const PRODUCT_OPTIONS = [
  { value: 'cuttings', label: 'Cuttings' },
  { value: 'shavings', label: 'Shavings' },
  { value: 'mats', label: 'Mats' },
  { value: '3/4_minus', label: '3/4" Minus' },
  { value: 'gravel', label: 'Gravel' },
  { value: 'sand', label: 'Sand' },
  { value: 'topsoil', label: 'Topsoil' },
  { value: 'other', label: 'Other' }
];

// Board Category options for the 3-column view
const BOARD_CATEGORIES = [
  { value: 'cuttings', label: 'Cuttings', color: 'border-blue-500', jobBorder: 'border-l-blue-500', textColor: 'text-blue-400' },
  { value: 'shavings', label: 'Shavings', color: 'border-amber-500', jobBorder: 'border-l-amber-500', textColor: 'text-amber-400' },
  { value: 'other', label: 'Other', color: 'border-green-500', jobBorder: 'border-l-green-500', textColor: 'text-green-400' }
];

export default function DispatchBoardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedDate, setSelectedDate] = useState(() => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
      const parsed = new Date(dateParam + 'T12:00:00');
      if (!isNaN(parsed.getTime())) return parsed;
    }
    return new Date();
  });
  const [mstToday, setMstToday] = useState('');
  const [mstTomorrow, setMstTomorrow] = useState('');
  
  const [assignments, setAssignments] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [units, setUnits] = useState([]);
  const [supportEquipment, setSupportEquipment] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);
  
  const [showDialog, setShowDialog] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendingWhatsApp, setSendingWhatsApp] = useState({});
  
  // Detail panel
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  
  // Search filters for available resources
  const [unitSearch, setUnitSearch] = useState('');
  const [driverSearch, setDriverSearch] = useState('');
  
  // Smart Dispatch dialog
  const [showSmartDispatch, setShowSmartDispatch] = useState(false);
  
  // Client ticket editing state
  const [editingTicketJobId, setEditingTicketJobId] = useState(null);
  const [editingTicketValue, setEditingTicketValue] = useState('');
  
  // Invoice number editing state
  const [editingInvoiceJobId, setEditingInvoiceJobId] = useState(null);
  const [editingInvoiceValue, setEditingInvoiceValue] = useState('');
  const [editingInvoiceNoteJobId, setEditingInvoiceNoteJobId] = useState(null);  // matches `${jobId}_${productType}` when a note editor is open
  const [editingInvoiceNoteValue, setEditingInvoiceNoteValue] = useState('');
  
  // Client ticket generation dialog
  const [showTicketDialog, setShowTicketDialog] = useState(false);
  const [ticketDialogJob, setTicketDialogJob] = useState(null);
  const [ticketDialogCategory, setTicketDialogCategory] = useState(null);
  const [ticketManualOverride, setTicketManualOverride] = useState(false);
  const [ticketManualItems, setTicketManualItems] = useState([{ description: '', qty: 0, uom: 'loads', rate: 0, unit_name: '', driver_name: '' }]);
  const [generatingTicket, setGeneratingTicket] = useState(false);
  const [ticketEligibility, setTicketEligibility] = useState(null);
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  
  // Drag and drop state
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverTarget, setDragOverTarget] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [activeId, setActiveId] = useState(null);
  
  // DndKit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );
  
  // Form state
  const [formData, setFormData] = useState({
    date: '',
    job_id: '',
    driver_id: '',
    unit_id: '',
    unit_name: '',
    support_equipment_id: '',
    support_equipment_name: '',
    load_time: '',
    time_type: 'load',
    product: '',
    board_category: 'other',
    pups: '',
    quads: '',
    vans: '',
    status: 'NOT_ASSIGNED',
    notes: ''
  });

  // Update selectedDate when URL ?date= param changes (e.g., from Invoice Review navigation)
  useEffect(() => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
      const parsed = new Date(dateParam + 'T12:00:00');
      if (!isNaN(parsed.getTime())) {
        setSelectedDate(parsed);
      }
    }
  }, [searchParams]);


  useEffect(() => {
    fetchMSTDates();
    fetchUnits();
  }, []);

  useEffect(() => {
    if (mstToday) {
      fetchAllData();
    }
  }, [selectedDate, mstToday]);

  // Helper to get ticket number for a specific product type + date (no legacy fallback)
  const getTicketForProductType = (job, productType, date) => {
    if (job.client_ticket_numbers && typeof job.client_ticket_numbers === 'object') {
      const key = productType?.toLowerCase();
      const dateStr = date ? getDateString(date) : getDateString(selectedDate);
      const dateKey = key ? `${key}_${dateStr}` : null;
      if (dateKey && dateKey in job.client_ticket_numbers) {
        return job.client_ticket_numbers[dateKey] || null;
      }
      return null;
    }
    return null;
  };

  // Helper to get invoice number for a specific product type + date (no legacy fallback)
  const getInvoiceForProductType = (job, productType, date) => {
    if (job.invoice_numbers && typeof job.invoice_numbers === 'object') {
      const key = productType?.toLowerCase();
      const dateStr = date ? getDateString(date) : getDateString(selectedDate);
      const dateKey = key ? `${key}_${dateStr}` : null;
      if (dateKey && dateKey in job.invoice_numbers) {
        return job.invoice_numbers[dateKey] || null;
      }
      return null;
    }
    return null;
  };

  // Retrieves the free-form invoice note attached to a job/product/date.
  // Used for entering additional ticket #s or ops comments alongside an
  // invoice number.
  const getInvoiceNoteForProductType = (job, productType, date) => {
    if (job.invoice_notes && typeof job.invoice_notes === 'object') {
      const key = productType?.toLowerCase();
      const dateStr = date ? getDateString(date) : getDateString(selectedDate);
      const dateKey = key ? `${key}_${dateStr}` : null;
      if (dateKey && dateKey in job.invoice_notes) {
        return job.invoice_notes[dateKey] || '';
      }
    }
    return '';
  };

  const fetchMSTDates = async () => {
    const token = localStorage.getItem('vaultai_token');
    const cacheBuster = Date.now();
    try {
      const res = await axios.get(`${API}/dispatch/dashboard?_t=${cacheBuster}`, {
        headers: { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' }
      });
      if (res.data.success) {
        const todayDate = res.data.today?.date || '';
        const tomorrowDate = res.data.tomorrow?.date || '';
        setMstToday(todayDate);
        setMstTomorrow(tomorrowDate);
        
        if (todayDate) {
          const [year, month, day] = todayDate.split('-').map(Number);
          const date = new Date(year, month - 1, day, 12, 0, 0);
          // Only set to today if there's no ?date= URL param
          if (!searchParams.get('date')) {
            setSelectedDate(date);
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch MST dates:', err);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchMSTDates();
    await fetchAllData();
    setRefreshing(false);
  };

  const getDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const goToMSTToday = () => {
    if (mstToday) {
      const [year, month, day] = mstToday.split('-').map(Number);
      const date = new Date(year, month - 1, day, 12, 0, 0);
      setSelectedDate(date);
    }
  };

  const isToday = () => getDateString(selectedDate) === mstToday;

  const fetchAllData = async () => {
    setLoading(true);
    const token = localStorage.getItem('vaultai_token');
    const dateStr = getDateString(selectedDate);
    const cacheBuster = Date.now();
    
    try {
      const [assignmentsRes, jobsRes, driversRes] = await Promise.all([
        axios.get(`${API}/dispatch/assignments?date=${dateStr}&_t=${cacheBuster}`, {
          headers: { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' }
        }),
        axios.get(`${API}/dispatch/jobs?active_only=true&_t=${cacheBuster}`, {
          headers: { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' }
        }),
        axios.get(`${API}/dispatch/drivers?active_only=true&check_date=${dateStr}&_t=${cacheBuster}`, {
          headers: { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' }
        })
      ]);
      
      if (assignmentsRes.data.success) {
        let fetchedAssignments = assignmentsRes.data.assignments || [];
        
        // Auto-update status to DRIVING for today's assignments if GPS shows moving
        if (isToday()) {
          fetchedAssignments = fetchedAssignments.map(a => {
            if (a.gps?.status === 'moving' && 
                a.status !== 'DRIVING' && 
                a.status !== 'COMPLETE') {
              updateAssignmentStatus(a.id, 'DRIVING', true);
              return { ...a, status: 'DRIVING' };
            }
            return a;
          });
        }
        
        setAssignments(fetchedAssignments);
      }
      if (jobsRes.data.success) {
        setJobs(jobsRes.data.jobs || []);
      }
      if (driversRes.data.success) {
        setDrivers(driversRes.data.drivers || []);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load dispatch data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchUnits = async () => {
    setLoadingUnits(true);
    const token = localStorage.getItem('vaultai_token');
    try {
      // First try custom units, fall back to Fleetio
      const customResponse = await axios.get(`${API}/dispatch/custom-units?active_only=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (customResponse.data.success && customResponse.data.units?.length > 0) {
        // Use custom units - map to expected format
        const customUnits = customResponse.data.units.map(u => ({
          id: u.id,
          name: u.unit_number,
          vehicle_type: u.description || '',
          owner: u.owner
        }));
        setUnits(customUnits);
        setSupportEquipment(customUnits);
      } else {
        // Fall back to Fleetio vehicles
        const response = await axios.get(`${API}/fleetio/vehicles?status=active`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          const allVehicles = response.data.vehicles || [];
          setUnits(allVehicles);
          setSupportEquipment(allVehicles);
        }
      }
    } catch (error) {
      console.error('Failed to fetch units:', error);
      // Try Fleetio as fallback on error
      try {
        const response = await axios.get(`${API}/fleetio/vehicles?status=active`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          const allVehicles = response.data.vehicles || [];
          setUnits(allVehicles);
          setSupportEquipment(allVehicles);
        }
      } catch (fallbackError) {
        console.error('Fleetio fallback also failed:', fallbackError);
      }
    } finally {
      setLoadingUnits(false);
    }
  };

  const updateAssignmentStatus = async (id, newStatus, silent = false) => {
    const token = localStorage.getItem('vaultai_token');
    try {
      await axios.put(`${API}/dispatch/assignments/${id}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!silent) {
        toast.success(`Status updated to ${STATUS_OPTIONS.find(s => s.value === newStatus)?.label}`);
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      if (!silent) {
        toast.error('Failed to update status');
      }
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem('vaultai_token');
    setSaving(true);
    
    try {
      const cleanedData = {};
      Object.keys(formData).forEach(key => {
        const value = formData[key];
        if (value !== null && value !== undefined && value !== '' && value !== '_none_') {
          if (key.endsWith('_id') && typeof value === 'number') {
            cleanedData[key] = String(value);
          } else {
            cleanedData[key] = value;
          }
        }
      });
      
      const payload = {
        ...cleanedData,
        date: getDateString(selectedDate)
      };
      
      if (editingAssignment) {
        const response = await axios.put(`${API}/dispatch/assignments/${editingAssignment.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          toast.success('Assignment updated');
        } else {
          toast.error(response.data.error || 'Failed to update assignment');
          return;
        }
      } else {
        const response = await axios.post(`${API}/dispatch/assignments`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          toast.success('Assignment created');
        } else {
          toast.error(response.data.error || 'Failed to create assignment');
          return;
        }
      }
      
      setShowDialog(false);
      setEditingAssignment(null);
      resetForm();
      fetchAllData();
    } catch (error) {
      console.error('Failed to save assignment:', error);
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (assignment) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) return;
    
    const token = localStorage.getItem('vaultai_token');
    try {
      await axios.delete(`${API}/dispatch/assignments/${assignment.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Assignment deleted');
      setSelectedAssignment(null);
      fetchAllData();
    } catch (error) {
      console.error('Failed to delete assignment:', error);
      toast.error('Failed to delete assignment');
    }
  };

  const sendToDriver = async (assignment) => {
    if (!assignment.driver?.phone) {
      toast.error('Driver has no phone number');
      return;
    }
    
    setSendingWhatsApp(prev => ({ ...prev, [assignment.id]: true }));
    const token = localStorage.getItem('vaultai_token');
    
    try {
      const response = await axios.post(
        `${API}/dispatch/assignments/${assignment.id}/send-whatsapp`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchAllData();
      } else {
        toast.error(response.data.error || 'Failed to send');
      }
    } catch (error) {
      console.error('Failed to send WhatsApp:', error);
      toast.error('Failed to send WhatsApp message');
    } finally {
      setSendingWhatsApp(prev => ({ ...prev, [assignment.id]: false }));
    }
  };

  // Send dispatch to ALL drivers on a job at once
  const sendToAllDrivers = async (jobId, jobAssignments) => {
    const driversWithPhones = jobAssignments.filter(a => a.driver?.phone);
    if (driversWithPhones.length === 0) {
      toast.error('No drivers with phone numbers on this job');
      return;
    }

    // Mark all as sending
    const sendingState = {};
    driversWithPhones.forEach(a => { sendingState[a.id] = true; });
    setSendingWhatsApp(prev => ({ ...prev, ...sendingState }));
    const token = localStorage.getItem('vaultai_token');

    try {
      const response = await axios.post(
        `${API}/dispatch/jobs/${jobId}/send-whatsapp-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        fetchAllData();
      } else {
        toast.error(response.data.error || 'Failed to send');
      }
    } catch (error) {
      console.error('Failed to send WhatsApp to all:', error);
      toast.error('Failed to send WhatsApp messages');
    } finally {
      const resetState = {};
      driversWithPhones.forEach(a => { resetState[a.id] = false; });
      setSendingWhatsApp(prev => ({ ...prev, ...resetState }));
    }
  };

  const openNew = () => {
    setEditingAssignment(null);
    resetForm();
    setFormData(prev => ({ ...prev, date: getDateString(selectedDate) }));
    setShowDialog(true);
  };

  const openEdit = (assignment) => {
    setEditingAssignment(assignment);
    setFormData({
      date: assignment.date,
      job_id: assignment.job_id || '',
      driver_id: assignment.driver_id || '',
      unit_id: assignment.unit_id || '',
      unit_name: assignment.unit_name || '',
      support_equipment_id: assignment.support_equipment_id || assignment.trailer_id || '',
      support_equipment_name: assignment.support_equipment_name || assignment.trailer_name || '',
      load_time: assignment.load_time || '',
      time_type: assignment.time_type || 'load',
      product: assignment.product || '',
      board_category: assignment.board_category || 'other',
      pups: assignment.pups || '',
      quads: assignment.quads || '',
      vans: assignment.vans || '',
      status: assignment.status || 'NOT_ASSIGNED',
      notes: assignment.notes || ''
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      date: getDateString(selectedDate),
      job_id: '',
      driver_id: '',
      unit_id: '',
      unit_name: '',
      support_equipment_id: '',
      support_equipment_name: '',
      load_time: '',
      time_type: 'load',
      product: '',
      board_category: 'other',
      pups: '',
      quads: '',
      vans: '',
      status: 'NOT_ASSIGNED',
      notes: ''
    });
  };

  const formatDateDisplay = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    setSelectedDate(newDate);
  };

  const getStatusColor = (status) => {
    const opt = STATUS_OPTIONS.find(s => s.value === status);
    return opt?.color || 'bg-slate-500';
  };

  const getStatusLabel = (status) => {
    const opt = STATUS_OPTIONS.find(s => s.value === status);
    return opt?.label || status;
  };

  const navigateToBOL = (assignment) => {
    const jobNumber = assignment.job?.job_number;
    const date = assignment.date;
    navigate('/dashboard/client-tickets', { 
      state: { 
        prefill: { job_number: jobNumber, date: date },
        openCreate: true
      }
    });
  };

  // Group assignments by board_category
  const getAssignmentsByCategory = (category) => {
    return assignments.filter(a => (a.board_category || 'other') === category);
  };

  // Format assignment display for the card
  const formatAssignmentDisplay = (assignment) => {
    const unitNum = assignment.unit_name || '';
    const supportEquip = assignment.support_equipment_name || assignment.trailer_name || '';
    const driverName = assignment.driver?.name || '';
    const loadTime = assignment.load_time || '';
    const timeLabel = assignment.time_type === 'leave' ? 'leave at' : 'load at';
    
    // Format: "224 V9 - Connie - load at 7am" or "leave at 6am"
    let display = unitNum;
    if (supportEquip) display += ` ${supportEquip}`;
    if (driverName) display += ` - ${driverName}`;
    if (loadTime) display += ` - ${timeLabel} ${loadTime}`;
    
    return display;
  };

  const handleManualTimesheet = async (assignmentId) => {
    const token = localStorage.getItem('vaultai_token');
    const current = assignments.find(a => a.id === assignmentId);
    const wasOn = !!(current?.timesheet_submitted || current?.has_timesheet);
    const newValue = !wasOn;
    // Optimistic UI
    setAssignments(prev => prev.map(a =>
      a.id === assignmentId ? { ...a, timesheet_submitted: newValue, has_timesheet: newValue } : a
    ));
    try {
      const res = await axios.put(`${API}/dispatch/assignments/${assignmentId}`,
        { timesheet_submitted: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.success === false) {
        throw new Error(res.data?.error || 'Update failed');
      }
      toast.success(newValue ? 'Timesheet marked submitted' : 'Timesheet override cleared');
      fetchAllData();
    } catch (e) {
      console.error('handleManualTimesheet error:', e);
      toast.error(`Failed to update: ${e?.response?.data?.error || e?.message || 'unknown'}`);
      fetchAllData();
    }
  };

  const handleDriverConfirm = async (assignmentId, confirmed) => {
    const token = localStorage.getItem('vaultai_token');
    // Toggle: if already in this state, clear it back to null
    const current = assignments.find(a => a.id === assignmentId);
    const newValue = current?.driver_confirmed === confirmed ? null : confirmed;
    // Optimistic UI update for instant feedback
    setAssignments(prev => prev.map(a => 
      a.id === assignmentId ? { ...a, driver_confirmed: newValue } : a
    ));
    try {
      const res = await axios.put(`${API}/dispatch/assignments/${assignmentId}`, 
        { driver_confirmed: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.success === false) {
        throw new Error(res.data?.error || 'Update failed');
      }
      toast.success(newValue === true ? 'Driver confirmed' : newValue === false ? 'Driver declined' : 'Cleared');
      fetchAllData();
    } catch (e) {
      console.error('handleDriverConfirm error:', e);
      toast.error(`Failed to update: ${e?.response?.data?.error || e?.message || 'unknown'}`);
      fetchAllData();
    }
  };

  // Get available units (not assigned today)
  const getAvailableUnits = () => {
    const assignedUnitNames = new Set(assignments.map(a => a.unit_name).filter(Boolean));
    return units.filter(u => !assignedUnitNames.has(u.name));
  };

  // Get available drivers (not assigned today)
  const getAvailableDrivers = () => {
    const assignedDriverIds = new Set(assignments.map(a => a.driver_id).filter(Boolean));
    return drivers.filter(d => !assignedDriverIds.has(d.id) && !d.on_time_off);
  };

  // DndKit drag and drop handlers
  const handleDragStartDndKit = (event) => {
    const { active } = event;
    console.log('DND-KIT DRAG START:', active.id, active.data.current);
    setActiveId(active.id);
    setIsDragging(true);
    setDraggedItem(active.data.current);
    
    // Safety timeout - auto-cancel drag after 30 seconds if still active
    setTimeout(() => {
      setIsDragging(prev => {
        if (prev) {
          console.log('Drag timeout - auto canceling');
          setActiveId(null);
          setDraggedItem(null);
          return false;
        }
        return prev;
      });
    }, 30000);
  };

  const handleDragCancel = useCallback(() => {
    console.log('DND-KIT DRAG CANCEL');
    setActiveId(null);
    setIsDragging(false);
    setDraggedItem(null);
  }, []);

  // Global escape key handler to cancel drag
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isDragging) {
        handleDragCancel();
      }
    };
    
    // Also clear drag state if mouse leaves window
    const handleMouseLeave = () => {
      if (isDragging) {
        handleDragCancel();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mouseleave', handleMouseLeave);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isDragging, handleDragCancel]);

  const handleDragEndDndKit = async (event) => {
    const { active, over } = event;
    console.log('=== DND-KIT DRAG END ===');
    console.log('Active:', active?.id, active?.data?.current);
    console.log('Over:', over?.id, over?.data?.current);
    
    // Always clear drag state first
    setActiveId(null);
    setIsDragging(false);
    setDraggedItem(null);
    
    if (!over) {
      console.log('No drop target (over is null)');
      return;
    }
    
    if (!active.data.current) {
      console.log('No drag data');
      return;
    }
    
    const token = localStorage.getItem('vaultai_token');
    const dragData = active.data.current;
    const dropId = over.id;
    
    console.log('Processing drop - dragData:', dragData, 'dropId:', dropId);
    
    try {
      // Parse drop target: could be "job:{jobId}:{category}", "category-{category}", or "assignment:{assignmentId}"
      if (dropId.startsWith('assignment:')) {
        console.log('DROP TARGET: Assignment');
        // Dropping onto an existing assignment - merge unit/driver
        const assignmentId = dropId.replace('assignment:', '');
        await handleDropOnAssignment(assignmentId, dragData, token);
      } else if (dropId.startsWith('job:')) {
        const parts = dropId.split(':');
        const jobId = parts[1];
        const category = parts[2] || 'other';
        
        // Find the job directly from jobs list
        const job = jobs.find(j => j.id === jobId);
        if (job) {
          // Find existing assignments for this job
          const jobAssignments = assignments.filter(a => a.job_id === jobId);
          await handleDropOnJobInternal({ job, assignments: jobAssignments }, category, dragData, token);
        } else {
          console.error('Job not found:', jobId);
          toast.error('Job not found');
        }
      } else if (dropId.startsWith('category-')) {
        const category = dropId.replace('category-', '');
        await handleDropOnCategoryInternal(category, dragData, token);
      }
    } catch (error) {
      console.error('Drop failed:', error);
      toast.error('Failed to assign');
    }
  };
  
  // Handle dropping a unit or driver onto an existing assignment
  const handleDropOnAssignment = async (assignmentId, dragData, token) => {
    const targetAssignment = assignments.find(a => a.id === assignmentId);
    if (!targetAssignment) {
      toast.error('Assignment not found');
      return;
    }
    
    console.log('DROP ON ASSIGNMENT:', assignmentId);
    console.log('Drag data:', dragData);
    console.log('Target assignment:', JSON.stringify(targetAssignment, null, 2));
    
    const updateData = {};
    
    if (dragData.type === 'unit') {
      // Dropping a unit onto an assignment - can have up to 2 units
      const hasUnit1 = targetAssignment.unit_id || targetAssignment.unit_name;
      const hasUnit2 = targetAssignment.support_equipment_id || targetAssignment.support_equipment_name;
      
      console.log('Has unit1?', hasUnit1, 'Has unit2?', hasUnit2);
      
      if (!hasUnit1) {
        // First slot is empty - use primary unit
        updateData.unit_id = String(dragData.id);
        updateData.unit_name = dragData.name;
        console.log('Assigning as primary unit');
      } else if (!hasUnit2) {
        // First slot filled, second empty - use support equipment
        updateData.support_equipment_id = String(dragData.id);
        updateData.support_equipment_name = dragData.name;
        console.log('Assigning as support equipment');
      } else {
        toast.error('This assignment already has 2 units assigned');
        return;
      }
    } else if (dragData.type === 'driver') {
      // Dropping a driver onto an assignment
      const hasDriver = targetAssignment.driver_id;
      console.log('Has driver?', hasDriver);
      if (hasDriver) {
        toast.error('This assignment already has a driver assigned');
        return;
      }
      updateData.driver_id = dragData.id;
    } else {
      toast.error('Cannot drop this item here');
      return;
    }
    
    console.log('Sending update:', updateData);
    
    try {
      const response = await axios.put(
        `${API}/dispatch/assignments/${assignmentId}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('Update response:', response.data);
      
      if (response.data.success) {
        const slotType = updateData.support_equipment_name ? 'support equipment' : 
                        updateData.unit_name ? 'primary unit' : 'driver';
        toast.success(`${dragData.name} added as ${slotType}`);
        fetchAllData();
      } else {
        toast.error(response.data.error || 'Update failed');
      }
    } catch (error) {
      console.error('Failed to update assignment:', error);
      toast.error('Failed to update: ' + (error.response?.data?.detail || error.message));
    }
  };
  
  // Internal handlers for drop logic
  const handleDropOnJobInternal = async (jobGroup, category, dragData, token) => {
    console.log('DROP ON JOB:', jobGroup.job?.job_number, 'dragData:', dragData);
    
    if (dragData.type === 'unit') {
      const payload = {
        date: getDateString(selectedDate),
        job_id: jobGroup.job.id,
        unit_name: dragData.name,
        unit_id: String(dragData.id),
        board_category: category,
        status: 'NOT_ASSIGNED'
      };
      
      const response = await axios.post(`${API}/dispatch/assignments`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`Unit ${dragData.name} assigned to Job #${jobGroup.job.job_number}`);
        fetchAllData();
      }
    } else if (dragData.type === 'driver') {
      const unassignedAssignment = jobGroup.assignments?.find(a => !a.driver_id);
      
      if (unassignedAssignment) {
        const response = await axios.put(
          `${API}/dispatch/assignments/${unassignedAssignment.id}`,
          { driver_id: dragData.id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.success) {
          toast.success(`${dragData.name} assigned to Job #${jobGroup.job.job_number}`);
          fetchAllData();
        }
      } else {
        const payload = {
          date: getDateString(selectedDate),
          job_id: jobGroup.job.id,
          driver_id: dragData.id,
          board_category: category,
          status: 'NOT_ASSIGNED'
        };
        
        const response = await axios.post(`${API}/dispatch/assignments`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.success) {
          toast.success(`${dragData.name} assigned to Job #${jobGroup.job.job_number}`);
          fetchAllData();
        }
      }
    }
  };
  
  // Quick add new assignment to a job
  const handleAddAssignmentToJob = async (job, category) => {
    const token = localStorage.getItem('vaultai_token');
    
    try {
      const payload = {
        date: getDateString(selectedDate),
        job_id: job.id,
        board_category: category,
        status: 'NOT_ASSIGNED'
      };
      
      const response = await axios.post(`${API}/dispatch/assignments`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`New assignment added to Job #${job.job_number}`);
        fetchAllData();
      } else {
        toast.error(response.data.error || 'Failed to add assignment');
      }
    } catch (error) {
      console.error('Failed to add assignment:', error);
      toast.error('Failed to add assignment');
    }
  };

  // Save client ticket number for a job (with product type + date)
  const handleSaveClientTicket = async (jobId, ticketNumber, productType = null) => {
    const token = localStorage.getItem('vaultai_token');
    try {
      const response = await axios.put(
        `${API}/dispatch/jobs/${jobId}`,
        { 
          client_ticket_number: ticketNumber,
          product_type: productType,
          ticket_date: getDateString(selectedDate)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Client ticket updated');
        setEditingTicketJobId(null);
        fetchAllData();
      } else {
        toast.error(response.data.error || 'Failed to update');
      }
    } catch (error) {
      console.error('Failed to update client ticket:', error);
      toast.error('Failed to update client ticket');
    }
  };

  // Save invoice number for a job (with product type + date)
  const handleSaveInvoice = async (jobId, invoiceNumber, productType = null) => {
    const token = localStorage.getItem('vaultai_token');
    try {
      const response = await axios.put(
        `${API}/dispatch/jobs/${jobId}`,
        { 
          invoice_number: invoiceNumber,
          product_type: productType,
          ticket_date: getDateString(selectedDate)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Invoice number updated');
        setEditingInvoiceJobId(null);
        fetchAllData();
      } else {
        toast.error(response.data.error || 'Failed to update');
      }
    } catch (error) {
      console.error('Failed to update invoice:', error);
      toast.error('Failed to update invoice number');
    }
  };

  // Save invoice note (free-form) — same date/product-type keying as invoices.
  // Used for extra ticket numbers, misc comments, ops notes.
  const handleSaveInvoiceNote = async (jobId, note, productType = null) => {
    const token = localStorage.getItem('vaultai_token');
    try {
      const response = await axios.put(
        `${API}/dispatch/jobs/${jobId}`,
        {
          invoice_note: note,
          product_type: productType,
          ticket_date: getDateString(selectedDate),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success('Note saved');
        setEditingInvoiceNoteJobId(null);
        fetchAllData();
      } else {
        toast.error(response.data.error || 'Failed to save note');
      }
    } catch (error) {
      console.error('Failed to save invoice note:', error);
      toast.error('Failed to save note');
    }
  };

  // Open client ticket generation dialog - check eligibility first
  const openTicketGenerationDialog = async (job, category) => {
    setTicketDialogJob(job);
    setTicketDialogCategory(category);
    setTicketManualOverride(false);
    setTicketManualItems([{ description: '', qty: 0, uom: 'loads', rate: 0, unit_name: '', driver_name: '' }]);
    setTicketEligibility(null);
    setCheckingEligibility(true);
    setShowTicketDialog(true);
    
    const token = localStorage.getItem('vaultai_token');
    
    try {
      // Get the assignments for this job on the selected date
      const jobAssignments = assignments.filter(a => 
        a.job_id === job.id && 
        (a.board_category || 'other') === category
      );
      
      // Check if we have drivers assigned
      const hasDrivers = jobAssignments.some(a => a.driver_id || a.driver?.id);
      const hasUnits = jobAssignments.some(a => a.unit_name || a.unit_id);
      
      // Check eligibility (timesheets, BOL)
      const response = await axios.post(
        `${API}/client-tickets/check-eligibility`,
        {
          job_number: job.job_number,
          job_id: job.id,
          date: getDateString(selectedDate),
          product_type: category,
          unit_name: jobAssignments.length > 0 ? jobAssignments[0].unit_name : null,
          unit_names: jobAssignments.map(a => a.unit_name).filter(Boolean),
          driver_names: jobAssignments.map(a => a.driver?.name).filter(Boolean)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Get counts from eligibility response
      const bolOnlyCount = response.data.bol_only_count || response.data.bol_count || 0;
      const scaleReceiptCount = response.data.scale_receipt_count || 0;
      const totalReceiptCount = response.data.bol_count || 0;
      
      // For "other" category: only BOL is required (no scale receipt needed)
      // For cuttings/shavings: need BOL + scale receipts
      const isOtherCategory = category === 'other';
      const hasRequiredReceipts = isOtherCategory 
        ? (bolOnlyCount > 0) 
        : (totalReceiptCount > 0);
      
      setTicketEligibility({
        hasDrivers,
        hasUnits,
        driverCount: jobAssignments.filter(a => a.driver_id || a.driver?.id).length,
        unitCount: jobAssignments.filter(a => a.unit_name || a.unit_id).length,
        timesheetCount: response.data.timesheet_count || 0,
        bolCount: totalReceiptCount,
        bolOnlyCount: bolOnlyCount,
        scaleReceiptCount: scaleReceiptCount,
        bolNumbers: response.data.bol_numbers || [],
        isOtherCategory,
        canGenerate: hasDrivers && (response.data.timesheet_count > 0) && hasRequiredReceipts
      });
    } catch (error) {
      console.error('Failed to check eligibility:', error);
      setTicketEligibility({
        hasDrivers: false,
        hasUnits: false,
        driverCount: 0,
        unitCount: 0,
        timesheetCount: 0,
        bolCount: 0,
        canGenerate: false,
        error: 'Failed to check eligibility'
      });
    } finally {
      setCheckingEligibility(false);
    }
  };

  // Generate client ticket from dispatch board data
  const handleGenerateClientTicket = async () => {
    if (!ticketDialogJob) return;
    
    setGeneratingTicket(true);
    const token = localStorage.getItem('vaultai_token');
    
    try {
      // Get the assignments for this job on the selected date
      const jobAssignments = assignments.filter(a => 
        a.job_id === ticketDialogJob.id && 
        (a.board_category || 'other') === ticketDialogCategory
      );
      
      // Build the request payload
      const payload = {
        job_number: ticketDialogJob.job_number,
        job_id: ticketDialogJob.id,
        date: getDateString(selectedDate),
        product_type: ticketDialogCategory,
        unit_name: jobAssignments.length > 0 ? jobAssignments[0].unit_name : null,
        unit_names: jobAssignments.map(a => a.unit_name).filter(Boolean),
        driver_names: jobAssignments.map(a => a.driver?.name).filter(Boolean)
      };
      
      if (ticketManualOverride) {
        payload.manual_override = true;
        payload.manual_line_items = ticketManualItems.filter(li => li.description.trim());
      }
      
      // Call the client ticket creation API
      const response = await axios.post(
        `${API}/client-tickets`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const ticketNumber = response.data.ticket?.ticket_number;
        
        // Update the job with the new ticket number FOR THIS PRODUCT TYPE + DATE
        if (ticketNumber) {
          await axios.put(
            `${API}/dispatch/jobs/${ticketDialogJob.id}`,
            { 
              client_ticket_number: ticketNumber,
              product_type: ticketDialogCategory,
              ticket_date: getDateString(selectedDate)
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
        
        toast.success(`Client ticket ${ticketNumber} created!`);
        setShowTicketDialog(false);
        fetchAllData();
        
        // Optionally navigate to the client tickets page
        // navigate('/dashboard/client-tickets');
      } else {
        toast.error(response.data.error || 'Failed to create ticket');
      }
    } catch (error) {
      console.error('Failed to generate client ticket:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate client ticket');
    } finally {
      setGeneratingTicket(false);
    }
  };

  const handleDropOnCategoryInternal = async (category, dragData, token) => {
    console.log('DROP ON CATEGORY:', category, 'dragData:', dragData);
    
    if (dragData.type === 'unit') {
      const payload = {
        date: getDateString(selectedDate),
        unit_name: dragData.name,
        unit_id: String(dragData.id),
        board_category: category,
        status: 'NOT_ASSIGNED'
      };
      
      const response = await axios.post(`${API}/dispatch/assignments`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`Unit ${dragData.name} added to ${category}`);
        fetchAllData();
      }
    } else if (dragData.type === 'driver') {
      const payload = {
        date: getDateString(selectedDate),
        driver_id: dragData.id,
        board_category: category,
        status: 'NOT_ASSIGNED'
      };
      
      const response = await axios.post(`${API}/dispatch/assignments`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`${dragData.name} added to ${category}`);
        fetchAllData();
      }
    }
  };

  // Legacy drag handlers (keeping for backward compatibility)
  const handleDragStart = (e, item, type) => {
    console.log('DRAG START:', type, item.name || item.id);
    setIsDragging(true);
    setDraggedItem({ ...item, dragType: type });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ id: item.id, type }));
    if (e.target) {
      e.dataTransfer.setDragImage(e.target, 0, 0);
    }
  };

  const handleDragEnd = () => {
    console.log('DRAG END');
    setIsDragging(false);
    setDraggedItem(null);
    setDragOverTarget(null);
  };

  const handleDragOver = (e, targetId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverTarget !== targetId) {
      console.log('DRAG OVER:', targetId);
      setDragOverTarget(targetId);
    }
  };

  const handleDragLeave = () => {
    setDragOverTarget(null);
  };

  const handleDropOnJob = async (e, jobGroup, category) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverTarget(null);
    
    console.log('DROP ON JOB:', jobGroup.job.job_number, 'draggedItem:', draggedItem);
    
    if (!draggedItem) return;
    
    const token = localStorage.getItem('vaultai_token');
    
    try {
      if (draggedItem.dragType === 'unit') {
        // Create new assignment with this unit for the job
        const payload = {
          date: getDateString(selectedDate),
          job_id: jobGroup.job.id,
          unit_name: draggedItem.name,
          unit_id: String(draggedItem.id),
          board_category: category,
          status: 'NOT_ASSIGNED'
        };
        
        const response = await axios.post(`${API}/dispatch/assignments`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.success) {
          toast.success(`Unit ${draggedItem.name} assigned to Job #${jobGroup.job.job_number}`);
          fetchAllData();
        }
      } else if (draggedItem.dragType === 'driver') {
        // If job has assignments without drivers, assign to first one
        const unassignedAssignment = jobGroup.assignments.find(a => !a.driver_id);
        
        if (unassignedAssignment) {
          const response = await axios.put(
            `${API}/dispatch/assignments/${unassignedAssignment.id}`,
            { driver_id: draggedItem.id },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (response.data.success) {
            toast.success(`${draggedItem.name} assigned to Job #${jobGroup.job.job_number}`);
            fetchAllData();
          }
        } else {
          // Create new assignment with just the driver
          const payload = {
            date: getDateString(selectedDate),
            job_id: jobGroup.job.id,
            driver_id: draggedItem.id,
            board_category: category,
            status: 'NOT_ASSIGNED'
          };
          
          const response = await axios.post(`${API}/dispatch/assignments`, payload, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data.success) {
            toast.success(`${draggedItem.name} assigned to Job #${jobGroup.job.job_number}`);
            fetchAllData();
          }
        }
      } else if (draggedItem.dragType === 'assignment') {
        // Moving assignment between jobs
        const response = await axios.put(
          `${API}/dispatch/assignments/${draggedItem.id}`,
          { job_id: jobGroup.job.id, board_category: category },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.success) {
          toast.success(`Assignment moved to Job #${jobGroup.job.job_number}`);
          fetchAllData();
        }
      }
    } catch (error) {
      console.error('Drop failed:', error);
      toast.error('Failed to assign');
    }
  };

  const handleDropOnCategory = async (e, category) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverTarget(null);
    
    console.log('DROP ON CATEGORY:', category, 'draggedItem:', draggedItem);
    
    if (!draggedItem) return;
    
    const token = localStorage.getItem('vaultai_token');
    
    try {
      if (draggedItem.dragType === 'unit') {
        // Create new standalone assignment with unit
        const payload = {
          date: getDateString(selectedDate),
          unit_name: draggedItem.name,
          unit_id: String(draggedItem.id),
          board_category: category,
          status: 'NOT_ASSIGNED'
        };
        
        const response = await axios.post(`${API}/dispatch/assignments`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.success) {
          toast.success(`Unit ${draggedItem.name} added to ${category}`);
          fetchAllData();
        }
      } else if (draggedItem.dragType === 'driver') {
        // Create new standalone assignment with driver
        const payload = {
          date: getDateString(selectedDate),
          driver_id: draggedItem.id,
          board_category: category,
          status: 'NOT_ASSIGNED'
        };
        
        const response = await axios.post(`${API}/dispatch/assignments`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.success) {
          toast.success(`${draggedItem.name} added to ${category}`);
          fetchAllData();
        }
      } else if (draggedItem.dragType === 'assignment') {
        // Move assignment to different category
        const response = await axios.put(
          `${API}/dispatch/assignments/${draggedItem.id}`,
          { board_category: category },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.success) {
          toast.success(`Assignment moved to ${category}`);
          fetchAllData();
        }
      }
    } catch (error) {
      console.error('Drop failed:', error);
      toast.error('Failed to assign');
    }
  };

  if (loading && !assignments.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Detail Panel Component
  const DetailPanel = () => {
    if (!selectedAssignment) return null;
    const a = selectedAssignment;
    
    return (
      <div className="w-80 lg:w-96 border-l bg-card h-full overflow-y-auto shrink-0">
        <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-card z-10">
          <h3 className="font-semibold">Assignment Details</h3>
          <Button variant="ghost" size="sm" onClick={() => setSelectedAssignment(null)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="p-4 space-y-4">
          {/* Job Info */}
          {a.job && (
            <div className="space-y-2">
              <h4 className="font-medium text-xs text-muted-foreground flex items-center gap-2 uppercase tracking-wider">
                <Briefcase className="h-3 w-3" />
                Job Details
              </h4>
              <div className="bg-secondary/30 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-blue-400 font-bold text-lg">#{a.job.job_number}</span>
                  <Badge className={`${getStatusColor(a.status)} text-white text-xs`}>
                    {getStatusLabel(a.status)}
                  </Badge>
                </div>
                <div className="font-medium text-lg">{a.job.client_name}{a.job.site_name ? ` (${a.job.site_name})` : ''}</div>
                {a.job.site_name && (
                  <div className="text-sm flex items-center gap-1 text-muted-foreground">
                    <MapPin className="h-3 w-3" />
                    {a.job.site_name}
                  </div>
                )}
                {/* Contact info */}
                {(a.job.contact_name || a.job.contact_phone) && (
                  <div className="text-sm flex items-center gap-1 text-muted-foreground">
                    <Phone className="h-3 w-3" />
                    {a.job.contact_name}{a.job.contact_phone && ` ${a.job.contact_phone}`}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Operator & Unit */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <h4 className="font-medium text-xs text-muted-foreground flex items-center gap-2 uppercase tracking-wider">
                <User className="h-3 w-3" />
                Operator
              </h4>
              <div className="bg-secondary/30 rounded-lg p-3">
                {a.driver ? (
                  <>
                    <div className="font-medium">{a.driver.name}</div>
                    {a.driver.phone && (
                      <div className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                        <Phone className="h-3 w-3" />
                        {a.driver.phone}
                      </div>
                    )}
                  </>
                ) : (
                  <span className="text-muted-foreground text-sm">Not assigned</span>
                )}
              </div>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium text-xs text-muted-foreground flex items-center gap-2 uppercase tracking-wider">
                <Truck className="h-3 w-3" />
                Unit
              </h4>
              <div className="bg-secondary/30 rounded-lg p-3">
                {a.unit_name ? (
                  <>
                    <div className="font-medium">{a.unit_name}</div>
                    {a.gps && (
                      <Badge 
                        variant="outline" 
                        className={`text-xs mt-1 ${
                          a.gps.status === 'moving' 
                            ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                            : 'bg-slate-500/20 text-slate-400 border-slate-500/30'
                        }`}
                      >
                        {a.gps.status === 'moving' ? `${Math.round(a.gps.speed_kph)} km/h` : a.gps.status}
                      </Badge>
                    )}
                  </>
                ) : (
                  <span className="text-muted-foreground text-sm">Not assigned</span>
                )}
              </div>
            </div>
          </div>
          
          {/* Support Equipment */}
          {(a.support_equipment_name || a.trailer_name) && (
            <div className="space-y-2">
              <h4 className="font-medium text-xs text-muted-foreground uppercase tracking-wider">Support Equipment</h4>
              <div className="bg-secondary/30 rounded-lg p-3">
                <div className="font-medium">{a.support_equipment_name || a.trailer_name}</div>
              </div>
            </div>
          )}
          
          {/* Product/Material */}
          {a.product && (
            <div className="space-y-2">
              <h4 className="font-medium text-xs text-muted-foreground uppercase tracking-wider">Product/Material</h4>
              <div className="bg-secondary/30 rounded-lg p-3">
                <Badge className={`text-sm ${
                  a.product.toLowerCase() === 'cuttings' 
                    ? 'bg-amber-600 text-white' 
                    : a.product.toLowerCase() === 'shavings' 
                      ? 'bg-amber-500 text-black' 
                      : 'bg-slate-500 text-white'
                }`}>
                  {a.product.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </Badge>
              </div>
            </div>
          )}
          
          {/* Load / Leave Time */}
          {a.load_time && (
            <div className="space-y-2">
              <h4 className="font-medium text-xs text-muted-foreground uppercase tracking-wider">{a.time_type === 'leave' ? 'Leave Time' : 'Load Time'}</h4>
              <div className="bg-secondary/30 rounded-lg p-3">
                <span className="font-medium">{a.load_time}</span>
              </div>
            </div>
          )}
          
          {/* Directions */}
          {a.job?.directions && (
            <div className="space-y-2">
              <h4 className="font-medium text-xs text-muted-foreground flex items-center gap-2 uppercase tracking-wider">
                <MapPin className="h-3 w-3" />
                Directions
              </h4>
              <div className="bg-secondary/30 rounded-lg p-3 text-sm whitespace-pre-wrap">
                {a.job.directions}
              </div>
            </div>
          )}
          
          {/* Notes */}
          {a.notes && (
            <div className="space-y-2">
              <h4 className="font-medium text-xs text-muted-foreground flex items-center gap-2 uppercase tracking-wider">
                <MessageCircle className="h-3 w-3" />
                Notes
              </h4>
              <div className="bg-secondary/30 rounded-lg p-3 text-sm whitespace-pre-wrap">
                {a.notes}
              </div>
            </div>
          )}
          
          {/* WhatsApp Status */}
          {a.whatsapp_sent && (
            <div className={`p-3 rounded-lg text-sm flex items-center gap-2 ${
              a.pending_confirmation ? 'bg-yellow-500/10 text-yellow-500' : 
              a.confirmed_at ? 'bg-green-500/10 text-green-500' : 
              a.declined_at ? 'bg-red-500/10 text-red-500' : 'bg-green-500/10 text-green-500'
            }`}>
              {a.pending_confirmation ? (
                <><Clock className="h-4 w-4" /> Awaiting confirmation</>
              ) : a.confirmed_at ? (
                <><Check className="h-4 w-4" /> Confirmed via WhatsApp</>
              ) : a.declined_at ? (
                <><X className="h-4 w-4" /> Declined - reassign needed</>
              ) : (
                <><Check className="h-4 w-4" /> Sent to operator</>
              )}
            </div>
          )}
          
          {/* Actions */}
          <div className="flex flex-col gap-2 pt-4 border-t">
            <Button 
              className="w-full" 
              variant="outline"
              onClick={() => sendToDriver(a)}
              disabled={sendingWhatsApp[a.id] || !a.driver?.phone || a.status === 'COMPLETE'}
            >
              {sendingWhatsApp[a.id] ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Send to Operator
            </Button>
            
            {/* Always show Create Client Ticket button */}
            <Button 
              className="w-full bg-green-600 hover:bg-green-700 text-white" 
              onClick={() => navigateToBOL(a)}
            >
              <FileCheck className="h-4 w-4 mr-2" />
              Create Client Ticket
            </Button>
            
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => openEdit(a)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Button variant="outline" className="text-red-500 hover:text-red-400" onClick={() => handleDelete(a)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Job Card Component for the column view
  const JobCard = ({ assignment }) => {
    const hasJob = !!assignment.job;
    
    return (
      <div 
        className={`border-2 rounded-lg p-3 mb-2 cursor-pointer hover:bg-secondary/50 transition-colors ${
          selectedAssignment?.id === assignment.id ? 'ring-2 ring-blue-500 bg-blue-500/10' :
          (assignment.timesheet_submitted || assignment.has_timesheet) ? 'border-pink-400 bg-pink-500/5' :
          assignment.driver_confirmed === true ? 'border-green-400 bg-green-500/5' :
          assignment.driver_confirmed === false ? 'border-red-400 bg-red-500/5' :
          'border-border bg-card'
        }`}
        onClick={() => setSelectedAssignment(assignment)}
        data-testid={`assignment-card-${assignment.id}`}
      >
        {/* Job Header - if job is assigned */}
        {hasJob && (
          <div className="mb-2 pb-2 border-b border-border">
            <div className="text-sm">
              <span className="font-bold text-blue-400">Job #{assignment.job.job_number}</span>
              {assignment.job.client_name && (
                <span className="text-muted-foreground"> - {assignment.job.client_name}</span>
              )}
              {assignment.job.client_contact && (
                <span className="text-muted-foreground"> {assignment.job.client_contact}</span>
              )}
            </div>
            {assignment.notes && (
              <div className="text-xs text-muted-foreground mt-1 line-clamp-3">
                {assignment.notes}
              </div>
            )}
          </div>
        )}
        
        {/* Driver/Unit Line with Confirm/Decline */}
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium flex-1">
            {formatAssignmentDisplay(assignment)}
          </div>
          {(assignment.driver_id || assignment.driver?.id || assignment.driver?.name || assignment.driver_name) && (
            <div className="flex gap-1 shrink-0" onClick={e => e.stopPropagation()}>
              <button 
                data-testid={`confirm-driver-${assignment.id}`}
                onClick={() => handleDriverConfirm(assignment.id, true)}
                className={`h-6 w-6 rounded flex items-center justify-center text-xs transition-colors ${
                  assignment.driver_confirmed === true 
                    ? 'bg-green-500 text-white' 
                    : 'bg-muted/30 text-green-400 hover:bg-green-500/20'
                }`}
                title="Driver confirmed"
              >
                <Check className="h-3.5 w-3.5" />
              </button>
              <button 
                data-testid={`decline-driver-${assignment.id}`}
                onClick={() => handleDriverConfirm(assignment.id, false)}
                className={`h-6 w-6 rounded flex items-center justify-center text-xs transition-colors ${
                  assignment.driver_confirmed === false 
                    ? 'bg-red-500 text-white' 
                    : 'bg-muted/30 text-red-400 hover:bg-red-500/20'
                }`}
                title="Driver declined"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
        
        {/* Status badge */}
        <div className="mt-2 flex items-center gap-2">
          <Badge className={`${getStatusColor(assignment.status)} text-white text-xs`}>
            {getStatusLabel(assignment.status)}
          </Badge>
          {(assignment.timesheet_submitted || assignment.has_timesheet) && (
            <Badge variant="outline" className="text-xs bg-pink-500/20 text-pink-400 border-pink-500/30">
              Timesheet In
            </Badge>
          )}
          {assignment.driver_confirmed === true && !(assignment.timesheet_submitted || assignment.has_timesheet) && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleManualTimesheet(assignment.id);
              }}
              className="inline-flex items-center justify-center h-6 w-6 rounded border border-muted-foreground/40 text-muted-foreground hover:border-pink-400 hover:text-pink-400 transition-colors cursor-pointer"
              title="Manually mark timesheet submitted (admin override)"
              data-testid={`manual-timesheet-${assignment.id}`}
            >
              <Clock className="h-3.5 w-3.5" />
            </button>
          )}
          {assignment.gps?.status === 'moving' && isToday() && (
            <Badge variant="outline" className="text-xs bg-green-500/20 text-green-400 border-green-500/30">
              Moving
            </Badge>
          )}
        </div>
      </div>
    );
  };

  // Column Component
  const BoardColumn = ({ category }) => {
    const columnAssignments = getAssignmentsByCategory(category.value);
    
    // Group by job
    const jobGroups = {};
    const standaloneAssignments = [];
    
    // Group by job. Use the resolved job's canonical id (a.job.id) rather
    // than the raw stored a.job_id — when the backend auto-heals duplicate
    // dispatch_jobs records, the assignment's stored job_id may differ from
    // the canonical record's id while new assignments use the canonical id.
    // Grouping on a.job.id keeps both rows in the same job block.
    columnAssignments.forEach(a => {
      if (a.job_id && a.job) {
        const jobKey = a.job?.id || a.job_id;
        if (!jobGroups[jobKey]) {
          jobGroups[jobKey] = {
            job: a.job,
            assignments: []
          };
        }
        jobGroups[jobKey].assignments.push(a);
      } else {
        standaloneAssignments.push(a);
      }
    });
    
    // Get jobs without assignments in this category (to show as drop targets).
    // Build the exclusion set from BOTH the raw assignment.job_id and the
    // canonical resolved job.id so duplicate dispatch_jobs records don't
    // surface as "Other Active Jobs" alongside the real job block.
    const jobsWithAssignments = new Set();
    columnAssignments.forEach(a => {
      if (a.job_id) jobsWithAssignments.add(a.job_id);
      if (a.job?.id) jobsWithAssignments.add(a.job.id);
      if (a.job?.job_number) jobsWithAssignments.add(`#${a.job.job_number}`);
    });
    const jobsWithoutAssignments = jobs.filter(j =>
      j.is_active
      && !jobsWithAssignments.has(j.id)
      && !jobsWithAssignments.has(`#${j.job_number}`)
    );
    
    return (
      <DroppableZone 
        id={`category-${category.value}`}
        className={`flex-1 min-w-[280px] border-t-4 ${category.color} bg-secondary/20 rounded-lg overflow-hidden flex flex-col`}
      >
        {/* Column Header */}
        <div className="p-3 border-b bg-secondary/30 flex items-center justify-between sticky top-0">
          <h3 className="font-bold text-lg">{category.label}</h3>
          <Badge variant="outline">{columnAssignments.length}</Badge>
        </div>
        
        {/* Column Content */}
        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {/* Jobs with assignments */}
          {Object.values(jobGroups).map((group, idx) => (
            <DroppableZone 
              key={group.job.id || idx} 
              id={`job:${group.job.id}:${category.value}`}
              className="space-y-1"
            >
              {/* Job Header Card */}
              <div className={`border rounded-lg p-3 bg-card border-l-4 ${category.jobBorder}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm flex-1">
                    <span className={`font-bold ${category.textColor}`}>Job #{group.job.job_number}</span>
                    <span className="text-foreground"> - {group.job.client_name}</span>
                    {(group.job.contact_name || group.job.contact_phone) && (
                      <span className="text-muted-foreground">
                        {' - '}
                        {group.job.contact_name}
                        {group.job.contact_phone && ` ${group.job.contact_phone}`}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {group.assignments.length > 1 && group.assignments.some(a => a.driver?.phone) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs hover:bg-blue-500/20 hover:text-blue-400"
                        onClick={(e) => {
                          e.stopPropagation();
                          sendToAllDrivers(group.job.id, group.assignments);
                        }}
                        title="Send to all operators on this job"
                      >
                        <Send className="h-3 w-3 mr-1" />
                        Send All
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 hover:bg-green-500/20 hover:text-green-400"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddAssignmentToJob(group.job, category.value);
                      }}
                      title="Add new assignment to this job"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {/* Client Ticket Number - Click to Generate or Edit */}
                <div className="mt-2 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Ticket #:</span>
                    {getTicketForProductType(group.job, category.value, selectedDate) ? (
                      <span
                        className="text-xs cursor-pointer hover:text-blue-400 hover:underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingTicketJobId(`${group.job.id}_${category.value}`);
                          setEditingTicketValue(getTicketForProductType(group.job, category.value, selectedDate) || '');
                        }}
                      >
                        {getTicketForProductType(group.job, category.value, selectedDate)}
                      </span>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 px-2 text-xs text-blue-400 hover:text-blue-300 hover:bg-blue-500/20"
                        onClick={(e) => {
                          e.stopPropagation();
                          openTicketGenerationDialog(group.job, category.value);
                        }}
                      >
                        <Receipt className="h-3 w-3 mr-1" />
                        Generate Ticket
                      </Button>
                    )}
                    {editingTicketJobId === `${group.job.id}_${category.value}` && (
                      <div className="flex items-center gap-1">
                        <Input
                          type="text"
                          value={editingTicketValue}
                          onChange={(e) => setEditingTicketValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSaveClientTicket(group.job.id, editingTicketValue, category.value);
                            } else if (e.key === 'Escape') {
                              setEditingTicketJobId(null);
                            }
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          onPointerDown={(e) => e.stopPropagation()}
                          className="h-6 w-24 text-xs"
                          autoFocus
                          placeholder="Ticket #"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0"
                          onClick={() => handleSaveClientTicket(group.job.id, editingTicketValue, category.value)}
                        >
                          <Check className="h-3 w-3 text-green-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0"
                          onClick={() => setEditingTicketJobId(null)}
                        >
                          <X className="h-3 w-3 text-red-500" />
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {/* Invoice Number - Editable */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Invoice #:</span>
                    {editingInvoiceJobId === `${group.job.id}_${category.value}` ? (
                      <div className="flex items-center gap-1">
                        <Input
                          type="text"
                          value={editingInvoiceValue}
                          onChange={(e) => setEditingInvoiceValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSaveInvoice(group.job.id, editingInvoiceValue, category.value);
                            } else if (e.key === 'Escape') {
                              setEditingInvoiceJobId(null);
                            }
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          onPointerDown={(e) => e.stopPropagation()}
                          className="h-6 w-24 text-xs"
                          autoFocus
                          placeholder="Invoice #"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0"
                          onClick={() => handleSaveInvoice(group.job.id, editingInvoiceValue, category.value)}
                        >
                          <Check className="h-3 w-3 text-green-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0"
                          onClick={() => setEditingInvoiceJobId(null)}
                        >
                          <X className="h-3 w-3 text-red-500" />
                        </Button>
                      </div>
                    ) : (
                      <span
                        className="text-xs cursor-pointer hover:text-blue-400 hover:underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingInvoiceJobId(`${group.job.id}_${category.value}`);
                          setEditingInvoiceValue(getInvoiceForProductType(group.job, category.value, selectedDate) || '');
                        }}
                      >
                        {getInvoiceForProductType(group.job, category.value, selectedDate) || <span className="italic text-muted-foreground">Click to add</span>}
                      </span>
                    )}
                  </div>

                  {/* Invoice Note - free-form. Used for additional ticket #s
                      when one dispatch date has multiple client tickets, or
                      any misc ops comment tied to this job/product/date.
                      Uses uncontrolled input pattern (defaultValue + ref) so
                      the cursor doesn't jump when the parent (dnd-kit
                      DroppableZone) re-renders. */}
                  <div className="flex items-start gap-2">
                    <span className="text-xs text-muted-foreground mt-1 shrink-0">Notes:</span>
                    {editingInvoiceNoteJobId === `${group.job.id}_${category.value}` ? (
                      <InvoiceNoteEditor
                        initialValue={getInvoiceNoteForProductType(group.job, category.value, selectedDate) || ''}
                        onSave={(val) => handleSaveInvoiceNote(group.job.id, val, category.value)}
                        onCancel={() => setEditingInvoiceNoteJobId(null)}
                        testKey={`${group.job.id}-${category.value}`}
                      />
                    ) : (
                      <span
                        className="text-xs cursor-pointer hover:text-blue-400 hover:underline whitespace-pre-wrap flex-1 text-foreground"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingInvoiceNoteJobId(`${group.job.id}_${category.value}`);
                        }}
                        data-testid={`invoice-note-display-${group.job.id}-${category.value}`}
                      >
                        {getInvoiceNoteForProductType(group.job, category.value, selectedDate) || <span className="italic text-muted-foreground">Click to add</span>}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Driver assignments for this job */}
              {group.assignments.map(a => {
                // WhatsApp confirmation status colors (priority: declined > timesheet > confirmed > awaiting)
                const whatsappStatusClass = a.status === 'DECLINED' 
                  ? 'border-red-500 bg-red-500/15' 
                  : a.has_timesheet
                    ? 'border-blue-500 bg-blue-500/15'
                    : a.status === 'CONFIRMED' 
                      ? 'border-green-500 bg-green-500/15' 
                      : a.pending_confirmation 
                        ? 'border-orange-500 bg-orange-500/15' 
                        : '';
                
                return (
                <DroppableAssignmentCard 
                  key={a.id}
                  assignment={a}
                  className={`ml-4 border-2 rounded p-2 cursor-grab hover:bg-secondary/50 transition-colors text-sm ${
                    (a.timesheet_submitted || a.has_timesheet) ? 'border-pink-400 bg-pink-500/5' :
                    a.driver_confirmed === true ? 'border-green-400 bg-green-500/5' :
                    a.driver_confirmed === false ? 'border-red-400 bg-red-500/5' :
                    selectedAssignment?.id === a.id ? 'ring-2 ring-blue-500 bg-blue-500/10' : whatsappStatusClass || 'bg-card/50'
                  } ${draggedItem?.id === a.id ? 'opacity-50' : ''}`}
                >
                  <div 
                    onClick={(e) => {
                      if (!isDragging) {
                        setSelectedAssignment(a);
                      }
                    }}
                    data-testid={`assignment-card-${a.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <span className="font-medium flex-1">
                        {a.unit_name || <span className="text-amber-400 italic">No unit</span>}
                        {a.support_equipment_name ? ` ${a.support_equipment_name}` : ''} 
                        {' - '}
                        {a.driver?.name || <span className="text-amber-400 italic">No driver</span>}
                        {a.load_time && <span className="text-muted-foreground"> - {a.time_type === 'leave' ? 'leave' : 'load'} at {a.load_time}</span>}
                      </span>
                      {(a.driver_id || a.driver?.id || a.driver?.name) && (
                        <div className="flex gap-1 shrink-0" onClick={e => { e.stopPropagation(); }}>
                          <button onClick={(e) => { e.stopPropagation(); handleDriverConfirm(a.id, true); }}
                            className={`h-5 w-5 rounded flex items-center justify-center transition-colors ${
                              a.driver_confirmed === true ? 'bg-green-500 text-white' : 'bg-muted/30 text-green-400 hover:bg-green-500/20'
                            }`} title="Confirmed">
                            <Check className="h-3 w-3" />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); handleDriverConfirm(a.id, false); }}
                            className={`h-5 w-5 rounded flex items-center justify-center transition-colors ${
                              a.driver_confirmed === false ? 'bg-red-500 text-white' : 'bg-muted/30 text-red-400 hover:bg-red-500/20'
                            }`} title="Declined">
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                      {(a.timesheet_submitted || a.has_timesheet) && a.status !== 'DECLINED' && <span className="text-pink-400 text-xs ml-1">TS</span>}
                      {a.driver_confirmed === true && !(a.timesheet_submitted || a.has_timesheet) && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleManualTimesheet(a.id); }}
                          className="h-5 w-5 rounded flex items-center justify-center transition-colors shrink-0 bg-muted/30 text-muted-foreground hover:bg-pink-500/20 hover:text-pink-400"
                          title="Manually mark timesheet submitted (admin override)"
                          data-testid={`manual-timesheet-${a.id}`}
                        >
                          <Clock className="h-3 w-3" />
                        </button>
                      )}
                      {a.status === 'DECLINED' && <span className="text-red-400 text-xs">Declined</span>}
                    </div>
                  </div>
                </DroppableAssignmentCard>
              )})}
            </DroppableZone>
          ))}
          
          {/* Standalone assignments (no job) */}
          {standaloneAssignments.map(a => (
            <DroppableAssignmentCard 
              key={a.id}
              assignment={a}
              className={`border-2 rounded-lg p-3 hover:bg-secondary/50 transition-colors ${
                (a.timesheet_submitted || a.has_timesheet) ? 'border-pink-400 bg-pink-500/5' :
                a.driver_confirmed === true ? 'border-green-400 bg-green-500/5' :
                a.driver_confirmed === false ? 'border-red-400 bg-red-500/5' :
                selectedAssignment?.id === a.id ? 'ring-2 ring-blue-500 bg-blue-500/10' : 'bg-card'
              } ${draggedItem?.id === a.id ? 'opacity-50' : ''}`}
            >
              <div 
                onClick={(e) => {
                  if (!isDragging) {
                    setSelectedAssignment(a);
                  }
                }}
                data-testid={`assignment-card-${a.id}`}
              >
                <div className="flex items-center gap-2 text-sm">
                  <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <span className="font-medium flex-1">
                    {a.unit_name || <span className="text-amber-400 italic">No unit</span>}
                    {' - '}
                    {a.driver?.name || <span className="text-amber-400 italic">No driver</span>}
                  </span>
                  {(a.driver_id || a.driver?.id || a.driver?.name) && (
                    <div className="flex gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                      <button onClick={(e) => { e.stopPropagation(); handleDriverConfirm(a.id, true); }}
                        className={`h-5 w-5 rounded flex items-center justify-center transition-colors ${
                          a.driver_confirmed === true ? 'bg-green-500 text-white' : 'bg-muted/30 text-green-400 hover:bg-green-500/20'
                        }`} title="Confirmed">
                        <Check className="h-3 w-3" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); handleDriverConfirm(a.id, false); }}
                        className={`h-5 w-5 rounded flex items-center justify-center transition-colors ${
                          a.driver_confirmed === false ? 'bg-red-500 text-white' : 'bg-muted/30 text-red-400 hover:bg-red-500/20'
                        }`} title="Declined">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </div>
                <div className="mt-2 flex items-center gap-1 flex-wrap">
                  <Badge className={`${getStatusColor(a.status)} text-white text-xs`}>
                    {getStatusLabel(a.status)}
                  </Badge>
                  {(a.timesheet_submitted || a.has_timesheet) && <Badge variant="outline" className="text-xs bg-pink-500/20 text-pink-400 border-pink-500/30">TS</Badge>}
                  {a.driver_confirmed === true && !(a.timesheet_submitted || a.has_timesheet) && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleManualTimesheet(a.id); }}
                      className="h-5 w-5 rounded flex items-center justify-center transition-colors shrink-0 bg-muted/30 text-muted-foreground hover:bg-pink-500/20 hover:text-pink-400"
                      title="Manually mark timesheet submitted (admin override)"
                      data-testid={`manual-timesheet-${a.id}`}
                    >
                      <Clock className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            </DroppableAssignmentCard>
          ))}
          
          {/* Jobs without assignments - show as drop targets */}
          {jobsWithoutAssignments.length > 0 && columnAssignments.length > 0 && (
            <div className="border-t border-dashed border-muted-foreground/30 pt-3 mt-3">
              <p className="text-xs text-muted-foreground mb-2">Other Active Jobs:</p>
              {jobsWithoutAssignments.slice(0, 5).map(job => (
                <DroppableZone 
                  key={job.id} 
                  id={`job:${job.id}:${category.value}`}
                  className="mb-1"
                >
                  <div className={`border border-dashed rounded-lg p-2 bg-card/30 hover:bg-card/50 transition-colors border-l-2 ${category.jobBorder}`}>
                    <div className="text-xs">
                      <span className={`font-medium ${category.textColor}`}>Job #{job.job_number}</span>
                      <span className="text-muted-foreground"> - {job.client_name}</span>
                    </div>
                  </div>
                </DroppableZone>
              ))}
            </div>
          )}
          
          {columnAssignments.length === 0 && (
            <>
              <div className="text-center text-muted-foreground py-4">
                <ClipboardList className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Drop units or drivers here</p>
              </div>
              {/* Show jobs as drop targets when column is empty */}
              {jobsWithoutAssignments.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground mb-2">Or drop onto a job:</p>
                  {jobsWithoutAssignments.slice(0, 8).map(job => (
                    <DroppableZone 
                      key={job.id} 
                      id={`job:${job.id}:${category.value}`}
                      className=""
                    >
                      <div className={`border border-dashed rounded-lg p-2 bg-card/30 hover:bg-card/50 transition-colors border-l-2 ${category.jobBorder}`}>
                        <div className="text-xs">
                          <span className={`font-medium ${category.textColor}`}>Job #{job.job_number}</span>
                          <span className="text-muted-foreground"> - {job.client_name}</span>
                        </div>
                      </div>
                    </DroppableZone>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </DroppableZone>
    );
  };
  
  // Available Resources Column Component
  const AvailableResourcesColumn = () => {
    const availableUnits = getAvailableUnits();
    const availableDrivers = getAvailableDrivers();
    
    // Filter units based on search
    const filteredUnits = unitSearch 
      ? availableUnits.filter(u => 
          u.name?.toLowerCase().includes(unitSearch.toLowerCase()) ||
          u.vehicle_type?.toLowerCase().includes(unitSearch.toLowerCase())
        )
      : availableUnits;
    
    // Filter drivers based on search
    const filteredDrivers = driverSearch
      ? availableDrivers.filter(d => 
          d.name?.toLowerCase().includes(driverSearch.toLowerCase())
        )
      : availableDrivers;
    
    return (
      <div className="w-72 border-t-4 border-purple-500 bg-secondary/20 rounded-lg overflow-hidden flex flex-col">
        {/* Column Header */}
        <div className="p-3 border-b bg-secondary/30">
          <h3 className="font-bold text-lg text-purple-400">Available</h3>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          {/* Units Section */}
          <div className="border-b">
            <div className="p-2 bg-secondary/40 flex items-center justify-between sticky top-0 z-10">
              <span className="text-sm font-medium flex items-center gap-2">
                <Truck className="h-4 w-4" />
                Units
              </span>
              <Badge variant="outline" className="text-xs">{filteredUnits.length}/{availableUnits.length}</Badge>
            </div>
            {/* Search input */}
            <div className="p-2 bg-secondary/20 sticky top-8 z-10">
              <Input
                type="text"
                placeholder="Search units..."
                value={unitSearch}
                onChange={(e) => setUnitSearch(e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                className="h-7 text-xs"
              />
            </div>
            <div className="p-2 space-y-1 max-h-[30vh] overflow-y-auto">
              {filteredUnits.slice(0, 100).map(unit => (
                <DraggableItem 
                  key={unit.id} 
                  id={unit.id} 
                  type="unit" 
                  data={{ id: unit.id, name: unit.name, vehicle_type: unit.vehicle_type }}
                >
                  <div
                    className={`border rounded p-2 cursor-grab hover:bg-secondary/50 transition-colors text-sm ${
                      draggedItem?.id === unit.id && draggedItem?.type === 'unit' ? 'opacity-50' : 'bg-card'
                    }`}
                    data-testid={`available-unit-${unit.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <GripVertical className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                      <Truck className="h-3 w-3 text-blue-400 flex-shrink-0" />
                      <span className="font-medium truncate">{unit.name}</span>
                    </div>
                    {unit.vehicle_type && (
                      <div className="text-xs text-muted-foreground ml-6 truncate">
                        {unit.vehicle_type}
                      </div>
                    )}
                  </div>
                </DraggableItem>
              ))}
              {filteredUnits.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-2">
                  {unitSearch ? 'No matching units' : 'All units assigned'}
                </p>
              )}
            </div>
          </div>
          
          {/* Drivers Section */}
          <div>
            <div className="p-2 bg-secondary/40 flex items-center justify-between sticky top-0 z-10">
              <span className="text-sm font-medium flex items-center gap-2">
                <User className="h-4 w-4" />
                Drivers
              </span>
              <Badge variant="outline" className="text-xs">{filteredDrivers.length}/{availableDrivers.length}</Badge>
            </div>
            {/* Search input */}
            <div className="p-2 bg-secondary/20 sticky top-8 z-10">
              <Input
                type="text"
                placeholder="Search drivers..."
                value={driverSearch}
                onChange={(e) => setDriverSearch(e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                className="h-7 text-xs"
              />
            </div>
            <div className="p-2 space-y-1 max-h-[30vh] overflow-y-auto">
              {filteredDrivers.map(driver => (
                <DraggableItem 
                  key={driver.id} 
                  id={driver.id} 
                  type="driver" 
                  data={{ id: driver.id, name: driver.name }}
                >
                  <div
                    className={`border rounded p-2 cursor-grab hover:bg-secondary/50 transition-colors text-sm flex items-center gap-2 ${
                      draggedItem?.id === driver.id && draggedItem?.type === 'driver' ? 'opacity-50' : 'bg-card'
                    }`}
                    data-testid={`available-driver-${driver.id}`}
                  >
                    <GripVertical className="h-3 w-3 text-muted-foreground" />
                    <User className="h-3 w-3 text-green-400" />
                    <span className="font-medium">{driver.name}</span>
                  </div>
                </DraggableItem>
              ))}
              {filteredDrivers.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-2">
                  {driverSearch ? 'No matching drivers' : 'All drivers assigned'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <DndContext 
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStartDndKit}
      onDragEnd={handleDragEndDndKit}
      onDragCancel={handleDragCancel}
    >
    <div className="flex h-[calc(100vh-140px)] overflow-hidden" data-testid="dispatch-board-page">
      {/* Dragging indicator - click to cancel */}
      {isDragging && draggedItem && (
        <div 
          className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 cursor-pointer hover:bg-blue-700"
          onClick={handleDragCancel}
          title="Click to cancel drag"
        >
          Dragging: {draggedItem.name || draggedItem.id} ({draggedItem.type || draggedItem.dragType})
          <span className="ml-2 text-xs opacity-75">(click to cancel)</span>
        </div>
      )}
      
      {/* DragOverlay for visual feedback */}
      <DragOverlay>
        {activeId ? (
          <div className="bg-blue-600 text-white px-3 py-2 rounded shadow-lg">
            {draggedItem?.name || activeId}
          </div>
        ) : null}
      </DragOverlay>
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Header */}
        <div className="p-4 border-b">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-bold flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-purple-500" />
                Dispatch Board
                <span className="text-[10px] font-normal text-muted-foreground ml-1" title="Build version">v235</span>
              </h1>
              <div className="flex gap-1">
                <Link to="/dashboard/dispatch/jobs">
                  <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                    <Briefcase className="h-4 w-4 mr-1" />
                    Jobs
                  </Button>
                </Link>
                <Link to="/dashboard/dispatch/drivers">
                  <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                    <Users className="h-4 w-4 mr-1" />
                    Operators
                  </Button>
                </Link>
                <Link to="/dashboard/dispatch/units">
                  <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                    <Truck className="h-4 w-4 mr-1" />
                    Units
                  </Button>
                </Link>
                <Link to="/dashboard/dispatch/invoice-review">
                  <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                    <FileText className="h-4 w-4 mr-1" />
                    Invoice Review
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowSmartDispatch(true)} 
                data-testid="smart-dispatch-btn"
              >
                <Navigation className="h-4 w-4 mr-2" />
                Find Available Trucks
              </Button>
              <Button onClick={openNew} data-testid="add-assignment-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Assignment
              </Button>
            </div>
          </div>
          
          {/* Date Navigation */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => navigateDate(-1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="text-center min-w-48">
                <div className="font-medium">{formatDateDisplay(selectedDate)}</div>
                {getDateString(selectedDate) === mstToday && (
                  <div className="text-xs text-green-500">Today</div>
                )}
                {getDateString(selectedDate) === mstTomorrow && (
                  <div className="text-xs text-blue-500">Tomorrow</div>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={() => navigateDate(1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={goToMSTToday}
                data-testid="today-btn"
              >
                Today
              </Button>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="px-2"
                    title="Pick a date"
                    data-testid="date-picker-btn"
                  >
                    <CalendarIcon className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(d) => { if (d) setSelectedDate(d); }}
                    initialFocus
                    data-testid="dispatch-date-picker-calendar"
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {assignments.length} assignment{assignments.length !== 1 ? 's' : ''}
              </span>
              <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>
        
        {/* 4-Column Board with Drag & Drop */}
        <div className="flex-1 overflow-hidden p-4">
          <div className="flex gap-4 h-full overflow-x-auto">
            {BOARD_CATEGORIES.map(category => (
              <BoardColumn key={category.value} category={category} />
            ))}
            <AvailableResourcesColumn />
          </div>
        </div>
      </div>
      
      {/* Detail Panel */}
      <DetailPanel />
      
      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingAssignment ? 'Edit Assignment' : 'New Assignment'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            {/* Board Category Selection - NEW */}
            <div className="grid gap-2">
              <label className="text-sm font-medium">Board Category *</label>
              <Select
                value={formData.board_category || 'other'}
                onValueChange={(value) => setFormData(prev => ({ ...prev, board_category: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {BOARD_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Job Selection */}
            <div className="grid gap-2">
              <label className="text-sm font-medium">Job (Optional)</label>
              <Select
                value={formData.job_id || '_none_'}
                onValueChange={(value) => setFormData(prev => ({ ...prev, job_id: value === '_none_' ? '' : value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a job" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none_">-- No job selected --</SelectItem>
                  {(() => {
                    // Dedup jobs by job_number (matches Jobs page behavior).
                    // Some jobs (e.g. #689) live as separate docs per product type.
                    const seen = new Set();
                    return jobs
                      .filter(j => {
                        const key = j.job_number;
                        if (seen.has(key)) return false;
                        seen.add(key);
                        return true;
                      })
                      .map(job => (
                        <SelectItem key={job.id} value={job.id}>
                          #{job.job_number} - {job.client_name}
                          {job.site_name && ` (${job.site_name})`}
                        </SelectItem>
                      ));
                  })()}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {/* Driver */}
              <div className="grid gap-2">
                <label className="text-sm font-medium">Driver/Operator (Optional)</label>
                <Select
                  value={formData.driver_id || '_none_'}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, driver_id: value === '_none_' ? '' : value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select driver" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none_">-- No driver selected --</SelectItem>
                    {drivers.map(driver => (
                      <SelectItem key={driver.id} value={driver.id}>
                        <div className="flex items-center gap-2">
                          {driver.on_time_off && <AlertTriangle className="h-3 w-3 text-amber-500" />}
                          <span className={driver.on_time_off ? 'text-amber-500' : ''}>{driver.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Unit */}
              <div className="grid gap-2">
                <label className="text-sm font-medium">Unit (Optional)</label>
                <Select
                  value={formData.unit_name || formData.unit_id || '_none_'}
                  onValueChange={(value) => {
                    if (value === '_none_') {
                      setFormData(prev => ({ ...prev, unit_id: '', unit_name: '' }));
                    } else {
                      const unit = units.find(u => String(u.id) === value || u.name === value);
                      setFormData(prev => ({ 
                        ...prev, 
                        unit_id: unit?.id ? String(unit.id) : '', 
                        unit_name: unit?.name || value 
                      }));
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none_">-- No unit selected --</SelectItem>
                    {loadingUnits ? (
                      <div className="p-2 text-center">
                        <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                      </div>
                    ) : (
                      units.map(unit => (
                        <SelectItem key={unit.id} value={unit.name}>
                          {unit.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {/* Support Equipment */}
              <div className="grid gap-2">
                <label className="text-sm font-medium">Support Equipment (Optional)</label>
                <Select
                  value={formData.support_equipment_name || '_none_'}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, support_equipment_name: value === '_none_' ? '' : value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select support equipment" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none_">None</SelectItem>
                    {supportEquipment.map(equip => (
                      <SelectItem key={equip.id} value={equip.name}>
                        {equip.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Load / Leave Time */}
              <div className="grid gap-2">
                <label className="text-sm font-medium">Time Type & Value (Optional)</label>
                <div className="flex gap-2">
                  <Select
                    value={formData.time_type || 'load'}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, time_type: value }))}
                  >
                    <SelectTrigger className="w-32" data-testid="time-type-select">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="load">Load</SelectItem>
                      <SelectItem value="leave">Leave</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="time"
                    value={formData.load_time}
                    onChange={(e) => setFormData(prev => ({ ...prev, load_time: e.target.value }))}
                    className="flex-1"
                    data-testid="time-value-input"
                  />
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {/* Product */}
              <div className="grid gap-2">
                <label className="text-sm font-medium">Product/Material (Optional)</label>
                <Select
                  value={formData.product || '_none_'}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, product: value === '_none_' ? '' : value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none_">-- No product selected --</SelectItem>
                    {PRODUCT_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Status */}
              <div className="grid gap-2">
                <label className="text-sm font-medium">Status</label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${opt.color}`} />
                          {opt.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Notes */}
            <div className="grid gap-2">
              <label className="text-sm font-medium">Notes / Job Description</label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Add job details, directions, instructions..."
                rows={4}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                editingAssignment ? 'Update' : 'Create'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Smart Dispatch Dialog */}
      <SmartDispatchDialog
        open={showSmartDispatch}
        onOpenChange={setShowSmartDispatch}
        jobs={jobs}
        selectedDate={getDateString(selectedDate)}
        onSelectTruck={(truck) => {
          setFormData(prev => ({
            ...prev,
            unit_id: truck.unit_number,
            unit_name: truck.unit_number
          }));
          setShowSmartDispatch(false);
          setShowDialog(true);
          setEditingAssignment(null);
        }}
      />

      {/* Client Ticket Generation Dialog */}
      <Dialog open={showTicketDialog} onOpenChange={setShowTicketDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-orange-500" />
              Generate Client Ticket
            </DialogTitle>
          </DialogHeader>
          
          {ticketDialogJob && (
            <div className="space-y-4">
              <div className="p-4 bg-secondary/30 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Job #:</span>
                  <span className="font-medium">{ticketDialogJob.job_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Client:</span>
                  <span className="font-medium">{ticketDialogJob.client_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Site:</span>
                  <span className="font-medium">{ticketDialogJob.site_name || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Product Type:</span>
                  <Badge className="capitalize">{ticketDialogCategory}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Date:</span>
                  <span className="font-medium">{formatDateDisplay(selectedDate)}</span>
                </div>
              </div>
              
              {/* Eligibility Check Status */}
              {checkingEligibility ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  <span className="text-sm text-muted-foreground">Checking requirements...</span>
                </div>
              ) : ticketEligibility && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Requirements Check:</p>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      {ticketEligibility.hasDrivers ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <X className="h-4 w-4 text-red-500" />
                      )}
                      <span className={ticketEligibility.hasDrivers ? 'text-green-400' : 'text-red-400'}>
                        Drivers: {ticketEligibility.driverCount} assigned
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      {ticketEligibility.hasUnits ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <X className="h-4 w-4 text-red-500" />
                      )}
                      <span className={ticketEligibility.hasUnits ? 'text-green-400' : 'text-red-400'}>
                        Units: {ticketEligibility.unitCount} assigned
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      {ticketEligibility.timesheetCount > 0 ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <X className="h-4 w-4 text-red-500" />
                      )}
                      <span className={ticketEligibility.timesheetCount > 0 ? 'text-green-400' : 'text-red-400'}>
                        Timesheets: {ticketEligibility.timesheetCount} found
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      {ticketEligibility.isOtherCategory ? (
                        // "Other" category: only BOL required
                        <>
                          {ticketEligibility.bolOnlyCount > 0 ? (
                            <Check className="h-4 w-4 text-green-500" />
                          ) : (
                            <X className="h-4 w-4 text-red-500" />
                          )}
                          <span className={ticketEligibility.bolOnlyCount > 0 ? 'text-green-400' : 'text-red-400'}>
                            BOL: {ticketEligibility.bolOnlyCount} found (scale receipt not required)
                          </span>
                        </>
                      ) : (
                        // Cuttings/Shavings: BOL + Scale Receipts required
                        <>
                          {ticketEligibility.bolCount > 0 ? (
                            <Check className="h-4 w-4 text-green-500" />
                          ) : (
                            <X className="h-4 w-4 text-red-500" />
                          )}
                          <span className={ticketEligibility.bolCount > 0 ? 'text-green-400' : 'text-red-400'}>
                            Scale Receipts/BOL: {ticketEligibility.bolCount} found
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {!ticketEligibility.canGenerate && !ticketManualOverride && (
                    <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <p className="text-sm text-red-400 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        Cannot generate ticket - missing required data
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {ticketEligibility.isOtherCategory 
                          ? 'Please ensure timesheets and a Bill of Lading are submitted for this job/date.'
                          : 'Please ensure timesheets and scale receipts are submitted for this job/date.'}
                      </p>
                    </div>
                  )}
                  
                  {/* Manual Override */}
                  <div className="mt-3 border rounded-lg p-3 space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer" data-testid="dispatch-manual-override">
                      <input type="checkbox" checked={ticketManualOverride} onChange={e => setTicketManualOverride(e.target.checked)}
                        className="rounded border-input" />
                      <span className="text-sm font-medium">Manual Override</span>
                      <span className="text-xs text-muted-foreground">(skip data checks, enter line items)</span>
                    </label>
                    {ticketManualOverride && (
                      <div className="space-y-2 pt-2 border-t">
                        {ticketManualItems.map((li, idx) => (
                          <div key={idx} className="flex gap-1.5 items-end">
                            <div className="w-20"><input className="w-full h-7 rounded border border-input bg-background px-2 text-xs" placeholder="Driver" value={li.driver_name || ''}
                              onChange={e => { const n = [...ticketManualItems]; n[idx] = {...n[idx], driver_name: e.target.value}; setTicketManualItems(n); }} /></div>
                            <div className="w-16"><input className="w-full h-7 rounded border border-input bg-background px-2 text-xs" placeholder="Unit #" value={li.unit_name || ''}
                              onChange={e => { const n = [...ticketManualItems]; n[idx] = {...n[idx], unit_name: e.target.value}; setTicketManualItems(n); }} /></div>
                            <div className="flex-1"><input className="w-full h-7 rounded border border-input bg-background px-2 text-xs" placeholder="Description" value={li.description}
                              onChange={e => { const n = [...ticketManualItems]; n[idx] = {...n[idx], description: e.target.value}; setTicketManualItems(n); }} /></div>
                            <div className="w-16"><input className="w-full h-7 rounded border border-input bg-background px-2 text-xs" type="number" placeholder="Qty" value={li.qty}
                              onChange={e => { const n = [...ticketManualItems]; n[idx] = {...n[idx], qty: parseFloat(e.target.value)||0}; setTicketManualItems(n); }} /></div>
                            <div className="w-20">
                              <select className="w-full h-7 rounded border border-input bg-background px-1 text-xs" value={li.uom}
                                onChange={e => { const n = [...ticketManualItems]; n[idx] = {...n[idx], uom: e.target.value}; setTicketManualItems(n); }}>
                                <option value="loads">Loads</option><option value="tonnes">Tonnes</option><option value="yards">Yards</option>
                                <option value="hours">Hours</option><option value="each">Each</option>
                              </select>
                            </div>
                            <div className="w-20"><input className="w-full h-7 rounded border border-input bg-background px-2 text-xs" type="number" step="0.01" placeholder="Rate $" value={li.rate}
                              onChange={e => { const n = [...ticketManualItems]; n[idx] = {...n[idx], rate: parseFloat(e.target.value)||0}; setTicketManualItems(n); }} /></div>
                            <span className="text-xs font-mono w-16 text-right">${(li.qty * li.rate).toFixed(2)}</span>
                            {ticketManualItems.length > 1 && (
                              <button className="text-red-400 hover:text-red-300 h-7 w-7 flex items-center justify-center" onClick={() => setTicketManualItems(ticketManualItems.filter((_,i) => i !== idx))}>
                                <X className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        ))}
                        <div className="flex justify-between items-center">
                          <button className="text-xs text-primary hover:underline" onClick={() => setTicketManualItems([...ticketManualItems, { description: '', qty: 0, uom: 'loads', rate: 0, unit_name: '', driver_name: '' }])}>
                            + Add line
                          </button>
                          <span className="text-sm font-bold">Total: ${ticketManualItems.reduce((s, li) => s + (li.qty * li.rate), 0).toFixed(2)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTicketDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleGenerateClientTicket} 
              disabled={generatingTicket || checkingEligibility || (!ticketEligibility?.canGenerate && !ticketManualOverride)}
              className="bg-orange-600 hover:bg-orange-700 disabled:opacity-50"
            >
              {generatingTicket ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <Receipt className="h-4 w-4 mr-2" />
                  Generate Ticket
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </DndContext>
  );
}
